import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../store';
import { setStatus } from '../../store/userGenerationSlice';
import { addNumUsers } from '../../services/api';
import type { StatusColor } from './UserGenerationTypes';
import { useNewGui } from '../../hooks/useNewGui';
import { FiUserPlus } from 'react-icons/fi';

interface Props {
  infraServers: any[];
  updateStatus: (msg: string, color: StatusColor) => void;
}

const AddNUsersTab: React.FC<Props> = ({ infraServers, updateStatus }) => {
  const dispatch = useDispatch<AppDispatch>();
  const useNew = useNewGui();
  const inputClass = useNew
    ? "mt-1 w-full border border-border-strong rounded px-3 py-2 bg-surface text-ink-primary text-sm focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent"
    : "mt-1 w-full border rounded px-3 py-2";
  const labelClass = useNew ? "block text-sm font-medium text-ink-primary" : "block text-sm font-medium text-gray-700";
  const [targetSystem, setTargetSystem] = useState<string>('');
  const [numUser, setNumUser] = useState<number>(1);
  const [linuxGroup, setLinuxGroup] = useState<string>('ws');
  const [loginType, setLoginType] = useState<string>('vnc');
  const [prefix, setPrefix] = useState<string>('ws');
  const [expiry, setExpiry] = useState<string>('6m');
  const [indexStart, setIndexStart] = useState<number>(1);
  const [submitting, setSubmitting] = useState<boolean>(false);

  useEffect(() => {
    setPrefix(linuxGroup);
  }, [linuxGroup]);

  const handleSubmit = async () => {
    if (!targetSystem.trim()) {
      updateStatus('Please select a Target System (VNC FQDN).', 'red');
      return;
    }
    setSubmitting(true);
    try {
      const result = await addNumUsers({
        targetSystem: targetSystem.trim(),
        numUser,
        linuxgroup: linuxGroup,
        loginType,
        prefix: prefix || linuxGroup,
        expiry: expiry || '6m',
        index_start_number: indexStart,
      });
      if (result.blob && result.filename) {
        const url = URL.createObjectURL(result.blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = result.filename;
        a.click();
        URL.revokeObjectURL(url);
        dispatch(setStatus('Add N Users completed. File downloaded.'));
        updateStatus('Add N Users completed. File downloaded.', 'blue');
      } else if (result.success && (result.stdout || result.message)) {
        const msg = result.message || result.stdout || 'Command completed.';
        dispatch(setStatus(msg));
        updateStatus(msg, 'blue');
      } else if (!result.success) {
        updateStatus(result.message || result.stderr || 'Add N Users failed.', 'red');
      }
    } catch (err: any) {
      let msg = err.message || 'Add N Users request failed.';
      const data = err.response?.data;
      if (data) {
        if (typeof data === 'string') msg = data;
        else if (data.message) msg = data.message;
        else if (data instanceof ArrayBuffer) {
          try { msg = JSON.parse(new TextDecoder().decode(data)).message || msg; } catch (_) {}
        }
      }
      updateStatus(msg, 'red');
    } finally {
      setSubmitting(false);
    }
  };

  if (useNew) {
    return (
      <div className="mb-6 space-y-4 font-plex">
        <div className="bg-surface border border-border-subtle rounded-lg overflow-hidden shadow-sm max-w-2xl">
          {/* Colored title band — matches the confident color language used elsewhere now */}
          <div className="bg-gradient-to-r from-accent to-accent-hover px-5 py-3 flex items-center gap-2">
            <FiUserPlus className="text-white text-lg" />
            <h2 className="text-sm font-semibold text-white">Add N Users</h2>
          </div>
          <div className="p-5">
            <p className="text-xs text-ink-secondary mb-4">
              Generate a specified number of users on a target system. Output file contains login-ids, credentials, VNC login and command.
            </p>
            <div className="mb-4 rounded border border-warning/40 bg-warning/10 px-4 py-2 text-sm text-warning">
              Users generated here are <strong>not</strong> tracked through Unique users in this console. This functionality may be enhanced in future depending upon need.
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Target System (VNC FQDN) *</label>
                <select className={inputClass} value={targetSystem} onChange={(e) => setTargetSystem(e.target.value)}>
                  <option value="">Select VNC FQDN</option>
                  {infraServers.map((server: any) => {
                    const label = server.serverInfo || `${server.hostName || server.serverFqdn || ''}:${server.port || server.servicePort || ''}`;
                    return <option key={server.id || label} value={label}>{label}</option>;
                  })}
                </select>
              </div>
              <div>
                <label className={labelClass}>Number of users *</label>
                <input type="number" min={1} className={inputClass} value={numUser} onChange={(e) => setNumUser(Math.max(1, parseInt(e.target.value, 10) || 1))} />
              </div>
              <div>
                <label className={labelClass}>Linux Group</label>
                <select className={inputClass} value={linuxGroup} onChange={(e) => { setLinuxGroup(e.target.value); setPrefix(`${e.target.value}_`); }}>
                  <option value="ws">ws</option>
                  <option value="po">po</option>
                  <option value="up">up</option>
                  <option value="ts">ts</option>
                  <option value="el">el</option>
                </select>
              </div>
              <div>
                <label className={labelClass}>Login type</label>
                <select className={inputClass} value={loginType} onChange={(e) => setLoginType(e.target.value)}>
                  <option value="vnc">vnc</option>
                  <option value="putty">putty</option>
                  <option value="both">both</option>
                </select>
              </div>
              <div>
                <label className={labelClass}>Prefix</label>
                <input className={inputClass} placeholder="e.g. ws_" value={prefix} onChange={(e) => setPrefix(e.target.value)} />
              </div>
              <div>
                <label className={labelClass}>Expiry</label>
                <input className={inputClass} placeholder="e.g. 6m" value={expiry} onChange={(e) => setExpiry(e.target.value)} />
                <p className="text-xs text-ink-secondary mt-1">Default: 6m from today</p>
              </div>
              <div>
                <label className={labelClass}>Index start number</label>
                <input type="number" min={0} className={inputClass} value={indexStart} onChange={(e) => setIndexStart(Math.max(0, parseInt(e.target.value, 10) || 0))} />
              </div>
            </div>
            <div className="mt-4">
              <button
                onClick={handleSubmit}
                disabled={submitting || !targetSystem.trim()}
                className={`px-5 py-2 rounded text-sm font-medium transition-colors shadow-sm ${
                  submitting || !targetSystem.trim()
                    ? 'bg-surface-raised text-ink-secondary border border-border-subtle cursor-not-allowed'
                    : 'bg-accent text-white hover:bg-accent-hover'
                }`}
              >
                {submitting ? 'Generating...' : 'Generate N Users'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-6 space-y-4">
      <div className="bg-white border border-gray-200 rounded-lg p-4 max-w-2xl">
        <h2 className="text-sm font-semibold text-gray-700 mb-3">Add N Users</h2>
        <p className="text-xs text-gray-500 mb-4">
          Generate a specified number of users on a target system. Output file contains login-ids, credentials, VNC login and command.
        </p>
        <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-800">
          Users generated here are <strong>not</strong> tracked through Unique users in this console. This functionality may be enhanced in future depending upon need.
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Target System (VNC FQDN) *</label>
            <select
              className={inputClass}
              value={targetSystem}
              onChange={(e) => setTargetSystem(e.target.value)}
            >
              <option value="">Select VNC FQDN</option>
              {infraServers.map((server: any) => {
                const label = server.serverInfo || `${server.hostName || server.serverFqdn || ''}:${server.port || server.servicePort || ''}`;
                return <option key={server.id || label} value={label}>{label}</option>;
              })}
            </select>
          </div>
          <div>
            <label className={labelClass}>Number of users *</label>
            <input
              type="number" min={1} className={inputClass}
              value={numUser}
              onChange={(e) => setNumUser(Math.max(1, parseInt(e.target.value, 10) || 1))}
            />
          </div>
          <div>
            <label className={labelClass}>Linux Group</label>
            <select
              className={inputClass}
              value={linuxGroup}
              onChange={(e) => { setLinuxGroup(e.target.value); setPrefix(`${e.target.value}_`); }}
            >
              <option value="ws">ws</option>
              <option value="po">po</option>
              <option value="up">up</option>
              <option value="ts">ts</option>
              <option value="el">el</option>
            </select>
          </div>
          <div>
            <label className={labelClass}>Login type</label>
            <select className={inputClass} value={loginType} onChange={(e) => setLoginType(e.target.value)}>
              <option value="vnc">vnc</option>
              <option value="putty">putty</option>
              <option value="both">both</option>
            </select>
          </div>
          <div>
            <label className={labelClass}>Prefix</label>
            <input
              className={inputClass} placeholder="e.g. ws_"
              value={prefix} onChange={(e) => setPrefix(e.target.value)}
            />
          </div>
          <div>
            <label className={labelClass}>Expiry</label>
            <input
              className={inputClass} placeholder="e.g. 6m"
              value={expiry} onChange={(e) => setExpiry(e.target.value)}
            />
            <p className={useNew ? "text-xs text-ink-secondary mt-1" : "text-xs text-gray-500 mt-1"}>Default: 6m from today</p>
          </div>
          <div>
            <label className={labelClass}>Index start number</label>
            <input
              type="number" min={0} className={inputClass}
              value={indexStart}
              onChange={(e) => setIndexStart(Math.max(0, parseInt(e.target.value, 10) || 0))}
            />
          </div>
        </div>
        <div className="mt-4">
          <button
            onClick={handleSubmit}
            disabled={submitting || !targetSystem.trim()}
            className={useNew
              ? `px-4 py-2 rounded text-sm font-medium transition-colors ${
                  submitting || !targetSystem.trim()
                    ? 'bg-surface-raised text-ink-secondary border border-border-subtle cursor-not-allowed'
                    : 'bg-accent text-white hover:bg-accent-hover'
                }`
              : `px-4 py-2 rounded-lg ${
                  submitting || !targetSystem.trim()
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-green-600 text-white hover:bg-green-700'
                }`
            }
          >
            {submitting ? 'Generating...' : 'Generate N Users'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddNUsersTab;
